import { createFileRoute } from "@tanstack/react-router";
import type { JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import Document from "@repo/icons/default/Document";
import Left from "@repo/icons/default/Left";

import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  EmptyStateIcon,
  EmptyStateRoot,
  EmptyStateTitle,
} from "@/components/ui/empty-state/empty-state";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { useGraphQL, useInvalidateQuery } from "@/hooks/use-graphql";
import { TaskDetailAction } from "@/lib/enums/assist/task-detail-action";
import { taskDetailPageSearchSchema } from "@/lib/form-schemas/task-detail-page-search-schema";
import { TaskDetailActions } from "@/routes/settings/assist/tasks/-components/task-actions/task-detail-actions";
import { TaskDetailHistory } from "@/routes/settings/assist/tasks/-components/task-detail/task-detail-history";
import { TaskDetailPatient } from "@/routes/settings/assist/tasks/-components/task-detail/task-detail-patient";
import { TaskDetailStatus } from "@/routes/settings/assist/tasks/-components/task-detail/task-detail-status";
import { TaskDetailSummary } from "@/routes/settings/assist/tasks/-components/task-detail/task-detail-summary";

const TaskDetailPageQuery = graphql(/* GraphQL */ `
  query TaskDetailPageQuery($taskId: Int!) {
    nuntius_tarefa_by_pk(id: $taskId) {
      ...TaskDetailStatusFragment
      ...TaskDetailPatientFragment
      ...TaskDetailSummaryFragment
      ...TaskDetailHistoryFragment
      ...TaskDetailActionsFragment
    }
  }
`);

const TaskDetailPage = (): JSX.Element => {
  const { taskId } = Route.useParams();
  const { action } = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryResult = useGraphQL(TaskDetailPageQuery, { taskId });
  const invalidateTask = useInvalidateQuery(TaskDetailPageQuery, { taskId });
  const task = queryResult.data?.nuntius_tarefa_by_pk;

  const openStatusDrawer = (): void => {
    void navigate({ search: { action: TaskDetailAction.ChangeStatus } });
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" aria-label="Voltar" />
        <HeaderTitle title="Detalhes da tarefa" />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {task ? (
            <div className="flex flex-col gap-4">
              <TaskDetailStatus data={task} onChangeStatus={openStatusDrawer} />
              <TaskDetailPatient data={task} />
              <TaskDetailSummary data={task} />
              <TaskDetailHistory data={task} />
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center py-8">
              <EmptyStateRoot>
                <EmptyStateIcon icon={Document} fill="primary" />
                <EmptyStateTitle>Tarefa não encontrada</EmptyStateTitle>
              </EmptyStateRoot>
            </div>
          )}
        </AsyncDataWrapper>
      </Page>
      {task ? (
        <TaskDetailActions
          key={taskId}
          data={task}
          action={action}
          onStatusChange={invalidateTask}
        />
      ) : null}
    </>
  );
};

export const Route = createFileRoute("/settings/assist/tasks/$taskId/")({
  parseParams: (params) => ({
    taskId: z.int().parse(Number(params.taskId)),
  }),
  validateSearch: taskDetailPageSearchSchema,
  component: TaskDetailPage,
});
